import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetCurrentUserQuery } from '../redux/apis/endpoints/authApi';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const PrivateRoute = ({ component: Component, roles = [], ...rest }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  

  // Get user loading state to prevent premature phone verification redirects
  const { isLoading: isLoadingUser, data: userData } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated || !!user,
    refetchOnMountOrArgChange: false,
  });

  // Use userData from query if user is not available in Redux (after refresh)
  const currentUser = user || userData;

  // Helper function to check if user has the required role
  const hasRequiredRole = (userObj, requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No specific role required
    }

    if (!userObj) {
      return false;
    }

    // Check if user is a merchant
    const isMerchant = userObj.merchantInfo?.isMerchant && userObj.merchantConfirmed;

    // Check if user has the required role
    return requiredRoles.some(role => {
      switch (role) {
        case 'merchant':
          return isMerchant;
        case 'customer':
          return !isMerchant; // Anyone who is not a merchant is considered a customer
        default:
          return false;
      }
    });
  };

  return (
    <Route
      {...rest}
      render={props => {
        if (!isAuthenticated) {
          return (
            <Redirect
              to={{
                pathname: "/login",
                state: { from: props.location }
              }}
            />
          );
        }

        // Show loading spinner while user data is being fetched
        // This prevents the phone verification redirect on page refresh
        if (isLoadingUser || !currentUser) {
          return (
            <div className="min-h-screen flex items-center justify-center">
              <LoadingSpinner />
            </div>
          );
        }

        if (!hasRequiredRole(currentUser, roles)) {
          // Redirect to unauthorized page or dashboard based on user type
          const isMerchant = currentUser?.merchantInfo?.isMerchant && currentUser?.merchantConfirmed;
          return (
            <Redirect
              to={{
                pathname: "/dashboard",
                state: {
                  from: props.location,
                  error: `Access denied. This page is restricted to ${roles.join(' or ')} only.`
                }
              }}
            />
          );
        }

        // Phone verification is optional — users can access all features without it.
        // They can still verify their phone in dashboard settings when ready.

        return <Component {...props} />;
      }}
    />
  );
};

export default PrivateRoute; 